// Talks directly to the scanner API from the browser — prd.md §6 point 2: "kein Server-
// Zwischenschritt beim Embedder". No cookies/credentials are sent (`credentials: "omit"`),
// matching architecture.md §4.3's permissive, credential-free CORS setup.
import type { ApiError, ScanCreatedResponse, ScanRequest, ScanStatusResponse } from "@apc/shared-types";

export interface ApiClientConfig {
  baseUrl: string;
}

export type ApiResult<T> = { ok: true; data: T } | { ok: false; error: ApiError["error"] };

async function postJson<T>(url: string, body: unknown): Promise<ApiResult<T>> {
  try {
    const response = await fetch(url, {
      method: "POST",
      headers: { "content-type": "application/json" },
      credentials: "omit",
      body: JSON.stringify(body),
    });
    const data = (await response.json()) as T | ApiError;
    if (!response.ok) {
      return { ok: false, error: (data as ApiError).error ?? { code: "VALIDATION_ERROR", message: "Request failed" } };
    }
    return { ok: true, data: data as T };
  } catch {
    return { ok: false, error: { code: "VALIDATION_ERROR", message: "network_error" } };
  }
}

export async function createScan(config: ApiClientConfig, request: ScanRequest): Promise<ApiResult<ScanCreatedResponse>> {
  return postJson<ScanCreatedResponse>(`${config.baseUrl}/scan`, request);
}

export async function submitEmail(
  config: ApiClientConfig,
  scanId: string,
  email: string,
  trigger: "during_scan" | "post_report",
): Promise<ApiResult<{ ok: true }>> {
  return postJson(`${config.baseUrl}/scan/${scanId}/email`, { email, trigger });
}

export async function getScanStatus(
  config: ApiClientConfig,
  scanId: string,
  since: number,
): Promise<ApiResult<ScanStatusResponse>> {
  try {
    const response = await fetch(`${config.baseUrl}/scan/${scanId}?since=${since}`, { credentials: "omit" });
    const data = (await response.json()) as ScanStatusResponse | ApiError;
    if (!response.ok) {
      // Only a real 404 means "this permalink doesn't exist" — pass the server's own error
      // code through unchanged so the caller can tell that apart from every other failure.
      return { ok: false, error: (data as ApiError).error ?? { code: "VALIDATION_ERROR", message: "Request failed" } };
    }
    return { ok: true, data: data as ScanStatusResponse };
  } catch {
    // A thrown fetch (offline, DNS hiccup, CORS misconfiguration, JSON parse failure) is NOT
    // the same thing as "this scan doesn't exist" — conflating the two used to make a single
    // transient network blip during polling look identical to an expired/invalid permalink.
    return { ok: false, error: { code: "VALIDATION_ERROR", message: "network_error" } };
  }
}

export interface PollHandle {
  stop(): void;
}

const MAX_CONSECUTIVE_TRANSIENT_ERRORS = 5;
const TRANSIENT_RETRY_DELAY_MS = 2_000;

/**
 * Polls `GET /scan/:id` using the server's own recommended `nextPollMs` (architecture.md §3.2)
 * instead of a fixed client-side interval, until the scan reaches a terminal status or polling
 * is explicitly stopped. `onUpdate` is called on every successful poll (including the first).
 * `onError` fires only once polling actually gives up — a genuine 404 (SCAN_NOT_FOUND) gives up
 * immediately, but any other failure (a transient network blip, a momentary 5xx) is retried up
 * to `MAX_CONSECUTIVE_TRANSIENT_ERRORS` times first, so a real user on a flaky connection
 * doesn't see "permalink not found" for what's actually just one dropped request.
 */
export function pollScan(
  config: ApiClientConfig,
  scanId: string,
  onUpdate: (data: ScanStatusResponse) => void,
  onError: (error: ApiError["error"]) => void,
): PollHandle {
  let stopped = false;
  let since = 0;
  let consecutiveErrors = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;

  async function tick() {
    if (stopped) return;
    const result = await getScanStatus(config, scanId, since);
    if (stopped) return;

    if (!result.ok) {
      if (result.error.code === "SCAN_NOT_FOUND") {
        onError(result.error);
        return;
      }
      consecutiveErrors += 1;
      if (consecutiveErrors > MAX_CONSECUTIVE_TRANSIENT_ERRORS) {
        onError(result.error);
        return;
      }
      timer = setTimeout(tick, TRANSIENT_RETRY_DELAY_MS);
      return;
    }

    consecutiveErrors = 0;
    since = result.data.events.reduce((max, e) => Math.max(max, e.seq), since);
    onUpdate(result.data);
    if (result.data.status === "complete" || result.data.status === "partial" || result.data.status === "failed") {
      return;
    }
    timer = setTimeout(tick, result.data.nextPollMs);
  }

  void tick();

  return {
    stop() {
      stopped = true;
      if (timer) clearTimeout(timer);
    },
  };
}
